import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiRelight = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-relight',
			name: 'AI Relight — Change Photo Lighting in One Tap',
			description:
				'AI Relight gives any photo a full light edit — new contrast, tone, and color balance in seconds. Try Lift today.',
			url: 'https://liftapp.ai/ai-relight',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-relight#product' },
				{ '@id': 'https://liftapp.ai/ai-relight#howto' },
				{ '@id': 'https://liftapp.ai/ai-relight#benefits' },
				{ '@id': 'https://liftapp.ai/ai-relight#faq' },
				{ '@id': 'https://liftapp.ai/ai-relight#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-relight#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-relight#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-relight#product',
			name: 'AI Relight',
			description:
				'Change any photo\'s lighting instantly with Lift AI Relight. Upload your image, pick a relight style, and get a full light edit — new contrast, tone, and color balance in seconds.',
			url: 'https://liftapp.ai/ai-relight',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-relight#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Pick a Relight Style',
					text: 'Browse the style gallery and pick the mood you want for your photo.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Upload Your Photo to Start',
					text: 'Add a photo from your device, and Lift gets it ready for the relight.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Generate Your New Look',
					text: 'AI relights the entire photo to match your chosen style within moments.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download or Try Again',
					text: 'Save your result, or tap regenerate to try a different style instantly.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-relight#benefits',
			name: 'Who can benefit from AI Relight?',
			description:
				'Lift\'s AI Relight is useful for creators, casual photographers, hobbyists, businesses, and anyone who wants a consistent light and color look across their photos.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Instagram & TikTok creators — Get a consistent golden-hour or cinematic feed look without reshooting. Pick a style once and apply the same look across every post.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Casual selfie takers — Fix flat, dim indoor lighting on a portrait in seconds. One relight style brings back natural light and evens out skin tone.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Photography hobbyists — Recreate film-emulation looks like 35mm, Fujifilm, or G7X Cam without buying separate presets or gear. The preset gallery covers analog, cinematic, and flash styles in one tap.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small business & marketing teams — Give product or campaign photos a shared visual identity without booking a studio lighting setup. Apply the same relight style across a full batch of shots for a consistent finish.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Nostalgic photo editors — Give everyday snapshots a vintage, film-era glow for keepsakes or throwback posts. Presets like 1960s Filter, Photobooth Effect, and Sunset Flash recreate that analog feel instantly.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Relight by Lift',
				description:
					'Example of how Lift AI Relight changes the lighting, contrast, and color of a photo to match a chosen style.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-relight#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift AI Relight?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Relight is an image-to-image tool that changes a photo\'s lighting, contrast, and white balance to match a chosen style. It offers a range of presets covering different looks and effects.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift AI Relight work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Pick a relight style, upload a photo, and Lift\'s AI relights it to match your choice. Download the result, or try another style to compare.',
					},
				},
				{
					'@type': 'Question',
					name: 'What kind of lighting styles can I apply with AI Relight?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'AI Relight offers presets across categories like cinematic, film-emulation, flash, and glow-skin, plus more styles to explore. Each preset applies a distinct light, contrast, and white-balance treatment to the same photo.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Absolutely. All images generated or edited in Lift can be exported without watermark when you\'re on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-relight#why-lift',
			name: 'Why Lift\'s AI Relight stands out',
			description:
				'Lift\'s AI Relight combines professional-grade AI, intelligent rendering, and affordability — making it a trusted choice for millions of users worldwide.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Relight',
				description:
					'Comparison of Lift\'s AI Relight benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-relight#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-relight#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'InstallAction',
					target: [
						'https://play.google.com/store/apps/details?id=com.lift.android.app',
						'https://apps.apple.com/us/app/lift-ai-photo-video-editor/id1505322988?mt=8',
					],
				},
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Relight',
					item: 'https://liftapp.ai/ai-relight',
				},
			],
		},
	],
};
